import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { LookupSelect } from "@/components/app/LookupSelect";

const EMPTY = { name: "", aliases: [], pan: "", city: "", state: "", pincode: "", country: "India", region: "", segment: "", group_name: "", notes: "", registrations: [], sites: [], contacts: [] };
const FIELDS = [["pan", "PAN"], ["group_name", "Customer group"], ["segment", "Customer segment (LM default)"], ["city", "HQ city"], ["state", "HQ state"], ["region", "Region"], ["country", "HQ country"], ["pincode", "Pincode"]];
const NEW_CONTACT = () => ({ name: "", designation: "", phone: "", email: "" });
const CONTACT_COLS = [["name", "Name", "sm:col-span-3"], ["designation", "Designation", "sm:col-span-2"], ["phone", "Phone", "sm:col-span-2"], ["email", "Email", "sm:col-span-4"]];
const NEW_REG = () => ({ gstin: "", label: "", city: "", state: "", billing_address: "" });
const NEW_SITE = () => ({ name: "", address: "", city: "", state: "", pincode: "", map_link: "", gstin: "", consignee_name: "", contact_person: "", contact_phone: "" });
const REG_COLS = [["gstin", "GSTIN (blank if unregistered)", "sm:col-span-3 font-mono"], ["label", "Label", "sm:col-span-1"], ["city", "City", "sm:col-span-2"], ["state", "State", "sm:col-span-2"], ["billing_address", "Billing address", "sm:col-span-3"]];
const SITE_COLS = [["name", "Site name", "sm:col-span-3"], ["address", "Delivery address", "sm:col-span-9"], ["city", "City", "sm:col-span-3"], ["state", "State", "sm:col-span-3"], ["pincode", "Pincode", "sm:col-span-2"], ["map_link", "Google location (link or lat, long)", "sm:col-span-4"],
  ["gstin", "Site GSTIN", "sm:col-span-3 font-mono"], ["consignee_name", "Consignee name", "sm:col-span-3"], ["contact_person", "Site contact", "sm:col-span-3"], ["contact_phone", "Contact phone", "sm:col-span-2"]];

const SubList = ({ title, hint, rows, cols, make, onChange, onBlur, testid }) => (
  <div className="sm:col-span-2 border border-slate-200 rounded p-3" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div><div className="text-xs font-semibold uppercase tracking-wider text-slate-600">{title}</div><div className="text-[11px] text-slate-500">{hint}</div></div>
      <Button type="button" size="sm" variant="ghost" onClick={() => onChange([...rows, make()])} data-testid={`${testid}-add`}><Plus className="h-4 w-4 mr-1" />Add</Button>
    </div>
    {rows.map((r, i) => (
      <div key={r.id || `n${i}`} className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-2">
        {cols.map(([k, ph, cls]) => (
          <Input key={k} placeholder={ph} title={ph} aria-label={ph} value={r[k] || ""} className={cls} onBlur={onBlur} data-testid={`${testid}-${i}-${k}`}
            onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, [k]: k === "gstin" ? e.target.value.toUpperCase() : e.target.value } : x)))} />
        ))}
        <Button type="button" variant="ghost" size="icon" className="sm:col-span-1" onClick={() => onChange(rows.filter((_, j) => j !== i))} data-testid={`${testid}-${i}-remove`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
      </div>
    ))}
    {rows.length === 0 && <div className="text-xs text-slate-400">None yet</div>}
  </div>
);

const Matches = ({ title, items, tone }) => items.length > 0 && (
  <div className={`rounded border p-3 text-sm ${tone}`} data-testid="customer-dup-warning">
    <div className="flex items-center gap-2 font-medium mb-1"><AlertTriangle className="h-4 w-4" />{title}</div>
    {items.map((m) => <div key={m.id} className="text-xs font-mono">{m.code} · {m.name} · {m.gstin || "no GSTIN"} — {m.reason}{m.score ? ` (${m.score})` : ""}{m.active === false ? " · inactive" : ""}</div>)}
  </div>
);

export function CustomerFormDialog({ open, onOpenChange, customer, onSaved, prefill }) {
  const [f, setF] = useState(EMPTY);
  const [dups, setDups] = useState({ blocking: [], similar: [] });
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setF(customer ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, customer[k] ?? EMPTY[k]])) } : { ...EMPTY, registrations: [NEW_REG()], sites: [NEW_SITE()], contacts: [NEW_CONTACT()], ...Object.fromEntries(Object.entries(prefill || {}).filter(([, v]) => v)) });
      setDups({ blocking: [], similar: [] });
      setConfirm(false);
    }
  }, [open, customer, prefill]);

  const check = async () => {
    const gstins = f.registrations.map((r) => r.gstin).filter(Boolean);
    if (f.name.trim().length < 2 && !gstins.length) return;
    try {
      const r = await api.post("/customers/check-duplicates", { name: f.name, gstins, aliases: f.aliases, exclude_id: customer?.id });
      setDups(r.data);
    } catch {
      setDups({ blocking: [], similar: [] }); // duplicate check is advisory; the server re-checks on save
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, confirm_similar: confirm };
      const r = customer ? await api.put(`/customers/${customer.id}`, body) : await api.post("/customers", body);
      toast.success(customer ? "Customer updated" : `Customer ${r.data.code} created`);
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.matches) setDups(d.code === "duplicate" ? { blocking: d.matches, similar: [] } : { blocking: [], similar: d.matches });
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="customer-form-dialog">
        <DialogHeader><DialogTitle>{customer ? `Edit ${customer.code}` : "New customer"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Label>Company name *</Label><Input value={f.name} onChange={set("name")} onBlur={check} data-testid="customer-name-input" /></div>
          <div className="sm:col-span-2"><Label>Also known as (comma separated)</Label>
            <Input value={(f.aliases || []).join(", ")} onBlur={check} data-testid="customer-aliases-input"
              onChange={(e) => setF({ ...f, aliases: e.target.value.split(",").map((s) => s.trimStart()) })} /></div>
          <SubList title="GST registrations" hint="One per GSTIN / billing location. A company can hold GSTINs in several cities and states." rows={f.registrations} cols={REG_COLS} make={NEW_REG}
            onChange={(v) => setF({ ...f, registrations: v })} onBlur={check} testid="customer-registrations" />
          <SubList title="Delivery sites" hint="Plants, warehouses or DCs where goods are shipped. Site GSTIN, consignee and contact are used when this site is picked on an LM." rows={f.sites} cols={SITE_COLS} make={NEW_SITE}
            onChange={(v) => setF({ ...f, sites: v })} testid="customer-sites" />
          <SubList title="Contacts" hint="The first contact is used on order snapshots." rows={f.contacts} cols={CONTACT_COLS} make={NEW_CONTACT}
            onChange={(v) => setF({ ...f, contacts: v })} testid="customer-contacts" />
          {FIELDS.map(([k, l]) => <div key={k}><Label>{l}</Label>{["region", "segment"].includes(k) ? <LookupSelect type={k} value={f[k] || ""} onChange={(v) => setF({ ...f, [k]: v })} testid={`customer-${k}-input`} />
            : <Input value={f[k]} onChange={set(k)} data-testid={`customer-${k}-input`} />}</div>)}
          <div className="sm:col-span-2"><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="customer-notes-input" /></div>
        </div>
        <Matches title="Duplicate — this customer already exists" items={dups.blocking} tone="border-red-200 bg-red-50 text-red-800" />
        <Matches title="Similar customers found" items={dups.similar} tone="border-amber-200 bg-amber-50 text-amber-800" />
        {dups.similar.length > 0 && dups.blocking.length === 0 && (
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={confirm} onCheckedChange={(v) => setConfirm(!!v)} data-testid="customer-confirm-similar" />I checked — this is a different company</label>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="customer-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy || dups.blocking.length > 0} data-testid="customer-save-btn">{busy ? "Saving…" : "Save customer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
